/**
 * Hubwayz hotel money helpers.
 *
 * The customer ILS charge is the backend quote. This module does not run a
 * second fare engine. Display code may convert a server chargeIls into the
 * selected currency; it must not invent the charge from the supplier net.
 */

/** Admin /getRate currencies. Anything else cannot be charged. */
export const HUBWAYZ_FX_CURRENCIES = ["USD", "EUR", "GBP", "ILS"];

/** Matches AppContext FX_SOURCE_FALLBACK_DEFAULT. A fallback is not a valid rate. */
export const HUBWAYZ_FX_SOURCE_FALLBACK = "fallback_default";

/**
 * Hubwayz has a dedicated sale commission, separate from HR margin.
 * fixed_hotel_commission and public_hotel_commission_* are not this setting.
 */
export const HUBWAYZ_AGENT_MARKUP_UNSET = "unset";
export const HUBWAYZ_AGENT_COMMISSION_SOURCE = "hubwayz_hotel_commission";

/** Example net used only by the settings preview. Not a live booking price. */
export const HUBWAYZ_COMMISSION_PREVIEW_NET_ILS = 1120;

/** Used when HR_COMMISSION_HOTELS is missing, 0, negative, or not a valid percent. */
export const HUBWAYZ_HR_HOTELS_DEFAULT_PERCENT = 12;

/**
 * HotelsRiver hotel margin for Hubwayz.
 * A positive percent up to 100 from HR_COMMISSION_HOTELS wins.
 * The general HR_COMMISSION value is not a substitute.
 * @param {unknown} raw
 * @returns {number}
 */
export function resolveHubwayzEffectiveHrHotelsPercent(raw) {
  if (raw == null || raw === "") return HUBWAYZ_HR_HOTELS_DEFAULT_PERCENT;
  const n = typeof raw === "number" ? raw : Number(String(raw).trim());
  if (!Number.isFinite(n) || n <= 0 || n > 100) return HUBWAYZ_HR_HOTELS_DEFAULT_PERCENT;
  return n;
}

/** Admin save gate for the hotels margin. Empty means "not set" and is allowed. */
export function hubwayzHrHotelsMarginSaveError(value) {
  if (value === undefined || value === null || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n) || n <= 0) {
    return "מרווח HotelsRiver למלונות חייב להיות גדול מ־0%. לא ניתן לשמור 0% או ערך שלילי.";
  }
  if (n > 100) {
    return "מרווח HotelsRiver למלונות חייב להיות גדול מ־0 ולא גדול מ־100.";
  }
  return null;
}

const KNOWN = new Set(HUBWAYZ_FX_CURRENCIES);

const FX_BLOCK_REASONS = new Set([
  "unrecognized_currency",
  "missing_fx_rate",
  "invalid_fx_rate",
]);

function fail(reason, extra = {}) {
  return {
    ok: false,
    reason,
    supplierAmount: extra.supplierAmount ?? null,
    supplierCurrency: extra.supplierCurrency ?? null,
    fxRate: null,
    fxApplied: false,
    ilsSalePrice: null,
    hrFeeIls: null,
    clearingFeeIls: null,
    agentNetIls: null,
    agentMarkupIls: null,
    customerSaleIls: null,
    agentMarkupSource: null,
    agentCommissionType: null,
    agentCommissionValue: null,
    supplierCostIls: null,
    hrMarginIls: null,
    hrHotelsPercent: null,
    chargeIls: null,
  };
}

function zeroGetter() {
  return 0;
}

/**
 * One active type. Missing or invalid input is percentage 0, not a negative markup.
 * @param {object|null|undefined} raw
 */
export function normalizeHubwayzAgentCommission(raw) {
  if (raw == null) {
    return { type: "percentage", value: 0, source: HUBWAYZ_AGENT_MARKUP_UNSET };
  }
  const type = raw.type === "fixed_ils" || raw.hubwayz_hotel_commission_type === "fixed_ils"
    ? "fixed_ils"
    : raw.type === "percentage" || raw.hubwayz_hotel_commission_type === "percentage"
      ? "percentage"
      : null;
  const rawValue = raw.value != null ? raw.value : raw.hubwayz_hotel_commission_value;
  const value = Number(rawValue);
  if (
    !type ||
    !Number.isFinite(value) ||
    value < 0 ||
    (type === "percentage" && value > 100)
  ) {
    return { type: "percentage", value: 0, source: "invalid" };
  }
  return { type, value, source: HUBWAYZ_AGENT_COMMISSION_SOURCE };
}

/** Percentage is ceiled once on agent net. Fixed ILS is the value itself, once. */
export function hubwayzAgentMarkupIls(agentNetIls, commission) {
  const net = Number(agentNetIls);
  const normalized = normalizeHubwayzAgentCommission(commission);
  if (!Number.isFinite(net) || net < 0 || normalized.value === 0) return 0;
  if (normalized.type === "fixed_ils") return normalized.value;
  return Math.ceil(net * (normalized.value / 100));
}

/**
 * Fixed commission is applied once to the order net, not once per room.
 * Percentage is ceiled once on the summed agent net.
 */
export function priceHubwayzOrderAgentSale(roomAgentNets, commission) {
  const nets = (Array.isArray(roomAgentNets) ? roomAgentNets : [])
    .map((n) => Number(n))
    .filter((n) => Number.isFinite(n) && n > 0);
  const agentNetIls = nets.reduce((sum, n) => sum + n, 0);
  const normalized = normalizeHubwayzAgentCommission(commission);
  const agentMarkupIls = hubwayzAgentMarkupIls(agentNetIls, normalized);
  return {
    roomCount: nets.length,
    agentNetIls,
    agentMarkupIls,
    customerSaleIls: agentNetIls + agentMarkupIls,
    agentCommissionType: normalized.type,
    agentCommissionValue: normalized.value,
    agentMarkupSource: normalized.source,
  };
}

export function hubwayzAudiencePriceView(audience) {
  if (audience === "customer") {
    return {
      audience: "customer",
      showAgentNet: false,
      showAgentMarkup: false,
      showSupplierCost: false,
      showHrMargin: false,
      showFxRate: false,
      primaryLabel: "מחיר",
    };
  }
  return {
    audience: "agent",
    showAgentNet: true,
    showAgentMarkup: true,
    showSupplierCost: false,
    showHrMargin: false,
    showFxRate: false,
    agentNetLabel: "סה״כ נטו לסוכן",
    markupLabel: "עמלת המכירה שלי",
    customerSaleLabel: "מחיר מכירה ללקוח",
    customerSaleNote: "אינו סכום לתשלום לספק ואינו סכום החיוב של הסוכן",
  };
}

function ceilMoney(value) {
  return Math.ceil(Number(value) - 1e-9);
}

/**
 * HotelsRiver margin once, in the net currency.
 * Matches the quote: ceil the net, ceil the fee, then ceil the sum.
 * `price` is not an input. The result is not converted here.
 */
export function hubwayzAgentPriceInNetCurrency(amount, hrPercent) {
  const raw = Number(amount);
  if (!Number.isFinite(raw) || raw <= 0) return null;
  const net = ceilMoney(raw);
  const percent = resolveHubwayzEffectiveHrHotelsPercent(hrPercent);
  const fee = percent > 0 ? ceilMoney(net * (percent / 100)) : 0;
  return ceilMoney(net + fee);
}

/**
 * Hubwayz search/poll/prebook hotel money.
 * netPrice only. price is the supplier gross and is not a margin base.
 * @param {object|null|undefined} node
 */
export function readHubwayzSupplierMoney(node) {
  const net = node?.netPrice;
  const netAmt = Number(net?.amount);
  const amount = Number.isFinite(netAmt) && netAmt > 0 ? netAmt : null;
  const currency = amount != null ? String(net?.currency || "").trim().toUpperCase() || null : null;
  return { amount, currency };
}

export function hubwayzFxBlocksPayment(priced) {
  return !!priced && priced.ok === false && FX_BLOCK_REASONS.has(priced.reason);
}

/**
 * @param {object} input
 * @param {number} input.supplierAmount
 * @param {string} input.supplierCurrency
 * @param {Record<string, number>|null} [input.currencyRate] ILS per 1 unit, from getRate
 * @param {Record<string, string>|null} [input.currencyRateSource]
 */
export function resolveHubwayzHotelIlsPricing({
  supplierAmount,
  supplierCurrency,
  currencyRate = null,
  currencyRateSource = null,
  hrCommissionHotelsSetting,
  getHrCommissionPercent: _getHrCommissionPercent = zeroGetter,
  getHrCommissionPercentHotels = zeroGetter,
  getAgentCommissionPercent: _getAgentCommissionPercent = zeroGetter,
  getCreditCardFeePercent = zeroGetter,
  agentCommission = null,
} = {}) {
  void supplierAmount;
  void supplierCurrency;
  void currencyRate;
  void currencyRateSource;
  void hrCommissionHotelsSetting;
  void _getHrCommissionPercent;
  void getHrCommissionPercentHotels;
  void _getAgentCommissionPercent;
  void getCreditCardFeePercent;
  void agentCommission;
  return fail("server_quote_required");
}

/** Payment amount is the already-settled ILS charge. Do not multiply by a rate again. */
export function hubwayzSettledChargeIls(priced) {
  if (!priced?.ok) return null;
  const n = Number(priced.chargeIls);
  return Number.isFinite(n) && n > 0 ? n : null;
}
